// Global session + navigation state (Svelte 5 runes). A single `$state` object
// shared across components, mutating a field re-renders every reader. Replaces
// the old Pixi SceneManager: `route` is the active screen.

import type { DuelFormat, ServerMessage } from "@ygoevo/world-protocol";
import type { SpawnPoint } from "../world/mapGraph.ts";
import { spawnFor } from "../world/mapGraph.ts";
import { getToken } from "../auth.ts";

export type Route = "connecting" | "title" | "name" | "character" | "mode" | "freeduel" | "world" | "duel" | "deck" | "leaderboard" | "settings" | "modeditor" | "mapedit";

/** Default landing map after sign-in: Duel Academy entrance. */
export const HOME_MAP = "map_a01";
export type LoginOk = Extract<ServerMessage, { t: "LoginOk" }>;

export const session = $state({
  // With a stored token we boot straight into "connecting" and wait for the socket's
  // LoginOk (which routes us into the world), no token means show the login screen.
  // This stops the login terminal from flashing for already-signed-in returning players.
  route: (getToken() ? "connecting" : "title") as Route,
  /** The game mode the player picked this session at the Mode Select screen. "world" = the
   *  collectible MMORPG (overworld, owned cards). "free" = Free Duel (no overworld, all cards,
   *  latest rules with Extra Monster Zones, a separate deck). Drives which deck namespace the
   *  Deck Editor edits and where a finished duel returns to. */
  mode: "world" as DuelFormat,
  /** Avatar customizer open as an overlay on top of the world (not a separate route). */
  customizing: false,
  loginOk: null as LoginOk | null,
  mapId: "map_r01",
  spawn: null as SpawnPoint | null,
  /** Pending daily login-streak reward to celebrate (set on login, cleared once shown). The
   *  optional cosmetic is set on the day the reward is an item (the Gold Disk on day 2). */
  dailyReward: null as { streak: number; dp: number; cosmetic?: Extract<ServerMessage, { t: "DailyReward" }>["cosmetic"] } | null,
  /** Pending one-time beta welcome grant to celebrate (DP and/or Shards; either is 0 when already
   *  claimed). Set on login when something was granted, cleared once shown. */
  betaWelcome: null as { dp: number; shards: number } | null,
  /** Last sign-in rejection (e.g. the beta kill switch), shown on the title screen. Cleared on a
   *  successful login or a fresh attempt. */
  authError: null as string | null,
});

export function go(route: Route): void {
  session.route = route;
}

/** Resolve the required first-run gates before exposing either game mode. */
export function loginRoute(m: LoginOk): Route {
  if (m.needsName) return "name";
  if (m.needsCharacter) return "character";
  return "mode";
}

/** Record a successful login. New players pick a name, create their duelist, then choose a mode. */
export function onLogin(m: LoginOk): void {
  session.loginOk = m;
  session.route = loginRoute(m);
}

/** Commit the mode choice and enter it: World drops into the overworld lobby; Free Duel goes to the
 *  duel-only Free Duel lobby (no overworld). Called from the Mode Select screen. */
export function chooseMode(mode: DuelFormat): void {
  if (session.loginOk?.needsCharacter) {
    session.route = "character";
    return;
  }
  session.mode = mode;
  if (mode === "world") enterWorld(HOME_MAP);
  else session.route = "freeduel";
}

/** Where a finished duel (or backing out) should return to, given the active mode. */
export function afterDuelRoute(): Route {
  return session.mode === "free" ? "freeduel" : "world";
}

/** Navigate into a lobby/map. */
export function enterWorld(mapId: string, spawn?: SpawnPoint): void {
  if (session.loginOk?.needsCharacter) {
    session.route = "character";
    return;
  }
  session.mapId = mapId;
  session.spawn = spawn ?? spawnFor(mapId);
  session.route = "world";
}
